import { promptRefsHelpFor } from '../../../utils/promptRefsHelp'
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMotionToolOpen } from '../../../hooks/useMotionToolOpen'
import { useTranslation } from 'react-i18next'
import { GenerateProvider, useGenerate } from '../../../context/GenerateContext'
import { useVariant } from '../../../context/VariantContext'
import { opAvailable, providerOf } from '../utils/trackOperations'
import { isTtsRoute, modelsForAudioRoute, supportsTts } from '../utils/ttsModels'
import { useRecentGenerationsContext } from '../../../context/RecentGenerationsContext'
import { useProviders } from '../../../hooks/useProviders'
import type { Model } from '../../../data/models'
import type { GenerationCard } from '../../../types/generationCard'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { CreateVideoIcon } from '../../../components/CreateVideoIcon'
import Logo from '../../../icons/Logo'
import GenerateInputBar from './GenerateInputBar'
import GenerateToolMode from './GenerateToolMode'
import SunoSourceTrackPin from './SunoSourceTrackPin'
import PresentationResultCard from './PresentationResultCard'
import { useSunoCoverIndex } from '../../../context/SunoCoverIndexContext'
import CardModal from '../../../components/CardModal'
import Sheet from '../../../components/Sheet'
import CardGallery from '../../../components/CardGallery'
import Tooltip from '../../../components/Tooltip'
import SideTooltip from '../../../components/SideTooltip'
import { getSourceFileFromCard } from '../utils/uploadHelpers'
import { applyCardToGenerate } from '../logic/applyCard'
import { useAttachedStyleCard, detachStyleCard, useDetachOnModelMismatch, isAudioReferenceActive } from '../../../api/attachedStyleCard'
import { useGlobalGeneration } from '../../../context/GlobalGenerationContext'
import AttachedStyleCard from '../../../components/AttachedStyleCard'
import { useCardRecreate } from '../hooks/useCardRecreate'
import { useHeightMorph } from '../hooks/useHeightMorph'
import { ModelBadge } from './ModelBadge'
import BottomBarPopover from './BottomBarPopover'
import WidgetSectionNav from './WidgetSectionNav'
import { SECTION_MODES, useSectionNav } from '../hooks/useSectionNav'
import { modelModeLabel } from '../utils/modelModeLabel'
import ModelHelpSheet from './ModelHelpSheet'
import IdeaWizardSheet from './IdeaWizardSheet'
import ErrorBoundary from './ErrorBoundary'
import { SpoilerReveal } from './SpoilerReveal'
import { useCreateVideo } from '../../../hooks/useCreateVideo'
import SpotlightTour from '../../../components/SpotlightTour'
import { PreloaderDynamic } from '../../../components/Preloader'
import DotFieldLoader from '../../../components/DotFieldLoader'
import { RETRY_NOTE, RETRY_TITLE, taskTick } from '../../../api/taskTicks'
import { useTaskTicks } from '../../../hooks/useTaskTicks'
import { loadPendingSnapshot } from '../../../context/historyPersistence'
import { BOTS_WIDGET_TOUR_KEY, buildWidgetTourSteps } from '../../../data/widgetTourSteps'
import { whenPageSettled } from '../../../bootShell'

// ── Public API ────────────────────────────────────────────────────

import type { SunoTrack } from '../../../api/music'
import type { AudioOperation } from '../../../context/GenerateContext'

export interface GenerateWidgetHandle {
	applyCard: (card: GenerationCard) => Promise<void>
	/** Установить source-трек для параметрической операции Suno
	 *  (extend / cover / add_vocals / add_instrumental). */
	applySourceTrack: (track: SunoTrack, operation: AudioOperation) => void
	/** Выбрать голос (персону) для генерации — пишет settings.audioPersonaId. */
	applyPersona: (personaId: string | null) => void
}

interface Props {
	model: Model
	/**
	 * /generate: закрыть виджет (unmount через gw-slide-down анимацию).
	 * /bots/*:   свернуть виджет (без unmount, через isCollapsed=true).
	 * Семантика определяется тем, передан ли `isCollapsed` parent'ом.
	 */
	onClose?: () => void
	/**
	 * Controlled collapse. Если undefined — старое поведение (unmount
	 * через gw-slide-down). Если boolean — виджет всегда смонтирован,
	 * а коллапс/разворот делается через CSS-класс `.gw--collapsed`.
	 * Прокидывается из BotsMediaLayout. На /generate не передаётся.
	 */
	isCollapsed?: boolean
	/**
	 * Клик на chevron-кнопку когда `isCollapsed=true` — раскрывает.
	 */
	onExpandRequest?: () => void
	onModelChange?: (m: Model | null) => void
	/** Если передан — "Создать видео" вызывает его вместо navigate('/bots/video') */
	onCreateVideoRequest?: (imageUrl: string) => void
	/** Живая синхронизация выбранного голоса (settings.audioPersonaId) наружу —
	 *  лента «Мои голоса» на /bots/audio/library подсвечивает активную карточку.
	 *  Передаётся только из BotsMediaLayout; на /generate не нужен. */
	onPersonaChange?: (personaId: string | null) => void
}

const GenerateWidget = forwardRef<GenerateWidgetHandle, Props>(function GenerateWidget(
	{ model, onClose, isCollapsed, onExpandRequest, onModelChange, onCreateVideoRequest, onPersonaChange },
	ref,
) {
	// Симметрично Generate.tsx (/generate): уход с генерационной поверхности
	// /bots сбрасывает контекст карточки «Популярное», чтобы выбранная, но не
	// сгенерированная карточка не «протекла» в следующую сессию. key="bots-widget"
	// константный (см. BotsMediaLayout) → этот wrapper размонтируется ТОЛЬКО при
	// закрытии виджета (widgetModel→null) или уходе с /bots — не при смене модели
	// или сворачивании. Поэтому сброс не сработает между «клик карточки» и
	// «генерация». Смену модели / уход в другую нейронку внутри сессии ловит сам
	// трекер (model-gating); здесь — финальная очистка границы поверхности.
	// Снимаем пин «Ваш стиль» вместе с атрибуцией (единый канал).
	// НО: пока идёт генерация (globalGen.status === 'running') пин НЕ снимаем —
	// иначе при возврате на /bots-медиа виджет переоткрывается классическим
	// баром вместо tool-режима, и юзер теряет прогресс генерации из виду. Ref —
	// чтобы cleanup читал АКТУАЛЬНЫЙ статус на момент размонтирования, а не
	// захваченный при маунте.
	const globalGen = useGlobalGeneration()
	const genRunningRef = useRef(false)
	useEffect(() => {
		genRunningRef.current = globalGen.status === 'running'
	}, [globalGen.status])
	useEffect(
		() => () => {
			// Переход /bots → /generate — легитимный «продолжить с карточкой в
			// большом генераторе» (пин задуман видимым на /generate): detach тут
			// оставлял страничный GenerateToolMode с карточкой-фантомом в state
			// (его подписка на событие ставится ПОЗЖЕ cleanup'а — emit пролетал),
			// и крестик был no-op (current уже null → ранний return без emit).
			// Router обновляет URL до анмаунта — читаем свежий pathname.
			if (window.location.pathname.startsWith('/generate')) return
			if (!genRunningRef.current) detachStyleCard()
		},
		[],
	)
	return (
		<GenerateProvider model={model} onModelChange={onModelChange}>
			<GenerateWidgetContent
				ref={ref}
				onClose={onClose}
				isCollapsed={isCollapsed}
				onExpandRequest={onExpandRequest}
				onCreateVideoRequest={onCreateVideoRequest}
				onPersonaChange={onPersonaChange}
			/>
		</GenerateProvider>
	)
})

export default GenerateWidget

// ── Helpers ────────────────────────────────────────────────────────

function isVideoUrl(url: string): boolean {
	return /\.(mp4|webm|mov)(\?|$)/i.test(url)
}

// ── Inner content ─────────────────────────────────────────────────

const GenerateWidgetContent = forwardRef<
	GenerateWidgetHandle,
	{
		onClose?: () => void
		isCollapsed?: boolean
		onExpandRequest?: () => void
		onCreateVideoRequest?: (imageUrl: string) => void
		onPersonaChange?: (personaId: string | null) => void
	}
>(function GenerateWidgetContent(
	{ onClose, isCollapsed, onExpandRequest, onCreateVideoRequest, onPersonaChange },
	ref,
) {
	const { t } = useTranslation()
	const gen = useGenerate()
	const variant = useVariant()
	// Пин «Ваш стиль» (прикреплённая витринная карточка) + авто-открепление
	// при ручной смене модели — рецепт карточки сломан (grace-окно внутри).
	const attachedCard = useAttachedStyleCard()
	// Пока идёт генерация — не снимаем пин по mismatch: на возврате на /bots-медиа
	// модель ещё гидрируется из pending-генерации, транзиентный mismatch иначе
	// снял бы tool-режим до применения модели.
	const globalGen = useGlobalGeneration()
	useDetachOnModelMismatch(gen.selectedModel, globalGen.status === 'running')
	// Фокус-режим инструмента: карточка «Популярных» (images/video) с toolMode
	// раскрывает виджет в tool-layout вместо классического бара. В свёрнутом
	// состоянии — обычная полоса (для 3-зонного лэйаута нет места).
	// См. Generate.tsx: вид карточки положен и «голой» фото-модели.
	const photoOnlyModel = !!gen.selectedModel?.noPrompt && !!gen.requiresTwoImages
	const isToolMode = (!!attachedCard?.toolMode || photoOnlyModel) && !isCollapsed
	// Морф высоты панели при входе/выходе tool-режима (Artlist-эффект:
	// виджет плавно растягивается под инструмент и сжимается обратно).
	const inputColRef = useRef<HTMLDivElement>(null)
	useHeightMorph(inputColRef, isToolMode)
	const { recent, removeGeneration, pruneIfMissing } = useRecentGenerationsContext()
	// Индекс обложек для старых audio-карточек без previewUrl.
	const { getCover } = useSunoCoverIndex()
	const { widgetModels } = useProviders()
	const navigate = useNavigate()
	const { createVideoNavigate } = useCreateVideo()

	// ── Хэндофф стандартной генерации в студию /generate (решение владельца
	// 2026-07-30). Витрины /bots/{trends,images,video} выглядят как раньше, но
	// «Создать» в ОБЫЧНОМ виджете (без tool-карточки) уводит юзера на
	// /generate: генерация стартует здесь же (все данные в этом инстансе —
	// никаких гонок переноса), доводится фоновым циклом flow, а прогресс и
	// результат живут в студии. Tool-карточки, пайплайн-GTM, аудио и
	// презентации остаются на месте. Триггер — isLoading false→true: он
	// наступает строго ПОСЛЕ валидации, так что ошибки показываются на месте
	// и никуда не уводят.
	const handoffPrevLoadingRef = useRef(gen.isLoading)
	useEffect(() => {
		const started = !handoffPrevLoadingRef.current && gen.isLoading
		handoffPrevLoadingRef.current = gen.isLoading
		if (!started) return
		if (!/^\/bots\/(trends|images|video)(\/|$)/.test(window.location.pathname)) return
		if (attachedCard?.toolMode || photoOnlyModel) return
		const cat = gen.selectedModel?.category
		if (cat !== 'image' && cat !== 'video') return
		// Формат — студии под место будущего кадра: её снимок генерации в этот миг ещё пишется (владелец 24.10).
		navigate('/generate', { state: { genHandoff: { prompt: gen.prompt, kind: cat, aspectRatio: gen.settings.aspectRatio } } })
	}, [gen.isLoading, attachedCard?.toolMode, photoOnlyModel, gen.selectedModel?.category, gen.prompt, gen.settings.aspectRatio, navigate])

	const [resultModalOpen, setResultModalOpen] = useState(false)
	// Какой ролик открыт в модале из GTM: оригинал или версия «Наложить трек» (28.09). null — gen.result.
	const [openedResult, setOpenedResult] = useState<string | null>(null)
	const openToolResult = (url?: string) => {
		setOpenedResult(url ?? null)
		setResultModalOpen(true)
	}
	const closeResultModal = () => {
		setResultModalOpen(false)
		setOpenedResult(null)
	}
	const [isClosing, setIsClosing] = useState(false)
	const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false)
	const [activeCard, setActiveCard] = useState<GenerationCard | null>(null)
	const [historyOpen, setHistoryOpen] = useState(false)
	const [modalInitialTime, setModalInitialTime] = useState(0)

	const [tourVisible, setTourVisible] = useState(false)
	// «?»-справка модели (особенности + FAQ из админки) — открывается кнопкой
	// в ModelBadge; интерактивный тур теперь стартует из её CTA.
	const [helpOpen, setHelpOpen] = useState(false)
	const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null)
	// «Придумать идею» (Идея-мастер) — кнопка-искра в шапке виджета рядом с «?».
	const [ideaOpen, setIdeaOpen] = useState(false)
	const [ideaAnchor, setIdeaAnchor] = useState<HTMLElement | null>(null)

	// ── Голос подставлен в виджет (через карточку «Мои голоса») ──────────
	// Кратко «мигаем» полем нейросети (ModelBadge) и полем голоса
	// (suno-persona-btn), чтобы юзер увидел КУДА подставился голос. Тик
	// растёт на каждый applyPersona; эффект перезапускает CSS-анимацию
	// (снять класс → rAF → вернуть), иначе повторная подстановка не мигнёт.
	const [voiceFlashTick, setVoiceFlashTick] = useState(0)
	const [voiceFlashOn, setVoiceFlashOn] = useState(false)
	const triggerVoiceFlash = useCallback(() => setVoiceFlashTick((t) => t + 1), [])
	useEffect(() => {
		if (voiceFlashTick === 0) return
		setVoiceFlashOn(false)
		const raf = requestAnimationFrame(() => setVoiceFlashOn(true))
		const timer = setTimeout(() => setVoiceFlashOn(false), 760)
		return () => {
			cancelAnimationFrame(raf)
			clearTimeout(timer)
		}
	}, [voiceFlashTick])

	// Живая синхронизация выбранного голоса наружу (active-карточка в ленте
	// «Мои голоса»). Ref на колбэк — эффект зависит только от значения
	// audioPersonaId, не от identity пропа.
	const onPersonaChangeRef = useRef(onPersonaChange)
	useEffect(() => {
		onPersonaChangeRef.current = onPersonaChange
	}, [onPersonaChange])
	useEffect(() => {
		onPersonaChangeRef.current?.(gen.settings.audioPersonaId ?? null)
	}, [gen.settings.audioPersonaId])

	const openTour = useCallback(() => {
		if (isCollapsed && onExpandRequest) {
			onExpandRequest()
			setTimeout(() => setTourVisible(true), 360)
		} else {
			setTourVisible(true)
		}
	}, [isCollapsed, onExpandRequest])

	const openTourRef = useRef(openTour)
	useEffect(() => { openTourRef.current = openTour }, [openTour])

	// Подсказка новичку — когда страница показана и догружена, а не поверх первых секунд: на медленном
	// интернете она затемняла ленту ровно в момент, когда та наконец появлялась (замер 30.09).
	useEffect(() => {
		if (localStorage.getItem(BOTS_WIDGET_TOUR_KEY)) return
		return whenPageSettled(() => openTourRef.current(), 1500)
	}, [])

	const applyCardImpl = useCallback(
		async (card: GenerationCard) => {
			await applyCardToGenerate(
				card,
				{
					setPrompt: gen.setPrompt,
					resetUploads: gen.resetUploads,
					resetCardRefs: gen.resetCardRefs,
					resetResult: gen.resetResult,
					restoreResult: gen.restoreResult,
					restoreInputs: gen.restoreInputs,
					updateSetting: gen.updateSetting,
					handleImageUpload: gen.handleImageUpload,
					setIsUploading: gen.setIsUploading,
					applyDefaultReferenceVideo: gen.applyDefaultReferenceVideo,
					applyDefaultUploadedVideo: gen.applyDefaultUploadedVideo,
					applyDefaultUploadedImage: gen.applyDefaultUploadedImage,
					setShowStyleHint: gen.setShowStyleHint,
					setVariantCard: variant.setVariantCard,
					clearVariantCard: variant.clearVariantCard,
				},
				gen.selectedModel ? { requiresVideo: gen.selectedModel.requiresVideo } : null,
				{ getSourceFileFromCard },
			)
		},
		[
			gen.resetUploads,
			gen.resetCardRefs,
			gen.resetResult,
			gen.restoreResult,
			gen.restoreInputs,
			gen.setPrompt,
			gen.updateSetting,
			gen.handleImageUpload,
			gen.setIsUploading,
			gen.applyDefaultReferenceVideo,
			gen.applyDefaultUploadedVideo,
			gen.applyDefaultUploadedImage,
			gen.setShowStyleHint,
			variant.setVariantCard,
			variant.clearVariantCard,
			gen.selectedModel,
		],
	)

	const recentRef = useRef<HTMLDivElement>(null)
	const editorRowRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const row = editorRowRef.current
		if (!row) return
		const gm = row.querySelector<HTMLElement>('.gm')
		const badge = row.querySelector<HTMLElement>('.generate-model-badge')
		if (!gm || !recentRef.current) return
		const sync = () => {
			if (!recentRef.current) return
			recentRef.current.style.height = `${gm.offsetHeight}px`
			recentRef.current.style.marginTop = badge ? `${badge.offsetHeight}px` : '0'
		}
		sync()
		const ro = new ResizeObserver(sync)
		ro.observe(gm)
		if (badge) ro.observe(badge)
		return () => ro.disconnect()
	}, [])

	const handleClose = useCallback(() => {
		if (isCollapsed !== undefined) {
			onClose?.()
			return
		}
		setIsClosing(true)
		setTimeout(() => onClose?.(), 300)
	}, [onClose, isCollapsed])

	// Навигация по разделам — общий модуль (тот же список и та же логика в
	// tool-режиме, см. WidgetSectionNav). `location` ниже нужен и другим
	// эффектам, поэтому остаётся здесь.
	const location = useLocation()
	const { currentCategory, currentMode, switchTo: handleModeSwitch } = useSectionNav()

	// URL → audioSubmode: страница /bots/audio/sounds переключает widget в
	// SFX-режим. Обратное переключение (уход на /bots/audio или /library) —
	// в music-режим. При смене вкладки эффект сам синхронизирует настройки.
	useEffect(() => {
		if (currentCategory !== 'audio') return
		const isSoundsRoute = location.pathname.startsWith('/bots/audio/sounds')
		const onTtsRoute = isTtsRoute(location.pathname)
		const targetSubmode = isSoundsRoute ? 'sounds' : onTtsRoute ? 'tts' : 'music'
		if (gen.settings.audioSubmode !== targetSubmode) {
			// Если в sounds-режим уходим — sourceTrack несовместим (см. setSourceTrack).
			// Сам setSettings ниже это не учитывает, поэтому чистим source-track заранее.
			if (isSoundsRoute && gen.sourceTrack) gen.setSourceTrack(null)
			gen.updateSetting('audioSubmode', targetSubmode)
		}

		// Suno SFX (sounds) работает ТОЛЬКО на V5 — форсим модель.
		// Обратно при выходе из sounds не переключаем: юзер мог специально
		// выбрать V5 для музыки и обратный свитч на дефолт его только бесит.
		if (isSoundsRoute && gen.selectedModel?.id.startsWith('suno-') && gen.selectedModel.id !== 'suno-v5') {
			const v5 = widgetModels.find((m) => m.id === 'suno-v5')
			if (v5) gen.setSelectedModel(v5)
		}
		// Озвучка — только у моделей озвучки (ElevenLabs). На /bots/audio/tts
		// форсим модель озвучки (список страницы — modelsForAudioRoute).
		if (onTtsRoute && !supportsTts(gen.selectedModel?.id)) {
			const tts = modelsForAudioRoute(widgetModels, location.pathname).find((m) => m.category === 'audio')
			if (tts) gen.setSelectedModel(tts)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, currentCategory])

	// Обратная синхронизация (сабмод → вкладка): выбор «Озвучка» (audioSubmode='tts')
	// в /bots/audio ВСЕГДА уводит на вкладку «Озвучка» (/bots/audio/tts). И наоборот —
	// уход из tts (Песня/Из фото-видео) на tts-вкладке возвращает на /bots/audio
	// (она tts-only). Лупа нет: URL-эффект выше ставит submode по роуту → совпадает → no-op.
	// Реагируем только на СМЕНУ режима: при монтировании режим — сохранённый с прошлого
	// раза, и прямой заход на /bots/audio/tts (ссылка, поиск) с сохранённой «Песней»
	// уводил на /bots/audio раньше, чем URL-эффект успевал включить озвучку.
	const prevSubmodeRef = useRef(gen.settings.audioSubmode)
	useEffect(() => {
		const prevSubmode = prevSubmodeRef.current
		prevSubmodeRef.current = gen.settings.audioSubmode
		if (prevSubmode === gen.settings.audioSubmode) return
		if (currentCategory !== 'audio') return
		if (!location.pathname.startsWith('/bots/audio')) return
		const onTts = isTtsRoute(location.pathname)
		const isTts = gen.settings.audioSubmode === 'tts'
		if (isTts && !onTts) navigate('/bots/audio/tts')
		else if (!isTts && onTts) navigate('/bots/audio')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gen.settings.audioSubmode])

	// Soundtrack («Из фото/видео») — только у Mureka. При переключении на не-Mureka
	// модель (Suno) сбрасываем submode в «Песня», иначе остаётся несовместимый UI
	// (лимит 1024, загрузка фото, плейсхолдер настроения), которого у Suno нет.
	// Сброс submode чинит сразу все гейты — они смотрят на settings.audioSubmode.
	useLayoutEffect(() => {
		const provider = providerOf(gen.selectedModel?.id)
		if (!opAvailable('soundtrack', provider) && gen.settings.audioSubmode === 'soundtrack') {
			gen.updateSetting('audioSubmode', 'music')
		}
		if (!opAvailable('tts', provider) && gen.settings.audioSubmode === 'tts') {
			gen.updateSetting('audioSubmode', 'music')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gen.selectedModel?.id, gen.settings.audioSubmode])

	const { handleCardAction } = useCardRecreate({
		selectedModelId: gen.selectedModel?.id,
		widgetModels,
		switchModel: gen.setSelectedModel,
		applyCard: applyCardImpl,
		onModelReset: gen.resetResult,
	})

	const handleCreateVideo = useCallback(
		(card: GenerationCard) => {
			setActiveCard(null)
			if (!card.previewUrl) return
			if (onCreateVideoRequest) {
				onCreateVideoRequest(card.previewUrl)
			} else {
				createVideoNavigate(card.previewUrl)
			}
		},
		[onCreateVideoRequest, createVideoNavigate],
	)

	// Destructure методов из `gen` для useImperativeHandle deps. ESLint
	// react-hooks/exhaustive-deps требует individual function references
	// (а не `gen` целиком — `gen` re-creates каждый render context → infinite
	// re-handle). Methods из useGenerate stable через own useCallback.
	const { setSourceTrack: genSetSourceTrack, setPrompt: genSetPrompt, updateSetting: genUpdateSetting,
		userUpdateSetting: genUserUpdateSetting } = gen
	useImperativeHandle(
		ref,
		() => ({
			applyCard: handleCardAction,
			applySourceTrack: (track, operation) => {
				// Prefill по операции:
				//   - extend          → переносим и title, и style (продолжаем «в том же духе»)
				//   - cover           → ТОЛЬКО title (юзер вписывает новый стиль вручную — это весь смысл cover)
				//   - add_vocals      → переносим style как контекст для вокала, title пусто
				//   - add_instrumental→ ничего не переносим (стиль = отдельное описание инструментала)
				genSetSourceTrack(track, operation)
				genSetPrompt('')
				const title = track.title?.trim() || ''
				const style = track.tags?.trim() || ''

				switch (operation) {
					case 'extend':
						if (title) genUpdateSetting('audioTitle', title)
						if (style) genUpdateSetting('audioStyle', style)
						break
					case 'cover':
						if (title) genUpdateSetting('audioTitle', `Cover — ${title}`)
						genUpdateSetting('audioStyle', '')
						break
					case 'add_vocals':
						genUpdateSetting('audioTitle', '')
						if (style) genUpdateSetting('audioStyle', style)
						break
					case 'add_instrumental':
						genUpdateSetting('audioTitle', '')
						genUpdateSetting('audioStyle', '')
						break
				}
			},
			applyPersona: (personaId) => {
				genUserUpdateSetting('audioPersonaId', personaId)  // голос выбрал человек
				triggerVoiceFlash()
			},
		}),
		[handleCardAction, genSetSourceTrack, genSetPrompt, genUpdateSetting, genUserUpdateSetting, triggerVoiceFlash],
	)

	const { isVideo, isAudio, isPresentation, requiresVideo, requiresImage, supportsImageInput, currentCost, costPending, selectedModel } = gen

	// Идея-мастер в АУДИО и ПРЕЗЕНТАЦИЯХ не показываем целиком: у музыкального
	// флоу свои креатив-инструменты (генерация текста песни, стили, персоны), а
	// у Gamma тему и структуру задают её собственные поля — «придумай за меня»
	// рядом с ними только путает (владелец 2026-08-18). Остаётся: изображения,
	// видео.
	const ideaAvailable = !isAudio && !isPresentation && !gen.selectedModel?.noPrompt
	// «Повторить движение» (23.10): пилюля — по рубильнику в админке («Меню сайта» → строка под «Видео»).
	const motionToolOpen = useMotionToolOpen()
	const motionFits = motionToolOpen && !isToolMode && !isAudio && !isPresentation

	const hasContent = !!(
		gen.prompt.trim() ||
		gen.result ||
		gen.uploadedImage ||
		gen.swapImage ||
		gen.uploadedVideo ||
		gen.endImage ||
		gen.referenceVideo ||
		gen.referenceImages?.length ||
		gen.omniReferenceImages?.length
	)
	// Прикреплённая карточка — тоже «контент»: иначе в tool-режиме (форма пуста
	// у гостя) «Очистить» была бы disabled и выйти из режима было бы нечем
	// (Очистить открепляет карточку → toolMode гаснет → классический бар).
	const clearDisabled = (!hasContent && !attachedCard) || gen.isLoading

	// ── Result slot ───────────────────────────────────────────────
	const audioTrackCount = gen.resultUrls?.length ?? (gen.result ? 1 : 0)
	// Провайдер не пропустил звук готового ролика — сервер пересоздаёт его (28.09): подсказка лоадера говорит почему дольше.
	useTaskTicks(gen.isLoading)
	const retrying = gen.isLoading && !!taskTick(loadPendingSnapshot()?.taskId)?.retrying
	const resultSlot = gen.isLoading ? (
		isAudio ? (
			<div className="gw__result-slot gw__result-slot--audio">
				<SideTooltip text={t('generate.audio.creatingTrack')}>
					<div className="gw__result-slot__audio-box gw__result-slot__audio-box--loading">
						<Icon id="gen-audio" size={20} />
						<PreloaderDynamic size={48} />
					</div>
				</SideTooltip>
			</div>
		) : isPresentation ? (
			<div className="gw__result-slot gw__result-slot--presentation">
				<SideTooltip text="Готовим презентацию — 1–2 мин">
					<div className="gw__result-slot__inner gw__result-slot__inner--loading">
						{/* Точечное поле заполняет ВЕСЬ тайл 50×50 — плотность решётки
						    подбирается от его размера (см. DotFieldLoader.dotsFor). */}
						<DotFieldLoader />
					</div>
				</SideTooltip>
			</div>
		) : (
			<div className="gw__result-slot">
				<SideTooltip text={retrying ? `${RETRY_TITLE} — ${RETRY_NOTE}` : t('generate.widget.waitGeneration')}>
					<div className="gw__result-slot__inner gw__result-slot__inner--loading">
						<DotFieldLoader />
					</div>
				</SideTooltip>
			</div>
		)
	) : gen.result ? (
		isAudio ? (
			<div className="gw__result-slot gw__result-slot--audio">
				{/* Стопка карточек с обложками треков — как карты в руке.
				    Front — первая обложка, back — вторая (если есть).
				    Hover меняет их местами с анимацией. Click по любой
				    карточке открывает CardModal с обоими треками. */}
				<SideTooltip text={audioTrackCount > 1 ? t('generate.audio.openTracks', { count: audioTrackCount }) : t('generate.audio.openTrack')}>
					<button
						className="gw__audio-cards"
						type="button"
						onClick={(e) => {
							const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
							setModalInitialTime(audio?.currentTime ?? 0)
							if (audio) {
								audio.pause()
								audio.currentTime = 0
							}
							setResultModalOpen(true)
						}}
						onMouseEnter={(e) => {
							const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
							audio?.play().catch(() => {})
						}}
						onMouseLeave={(e) => {
							const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
							if (audio) { audio.pause(); audio.currentTime = 0 }
						}}
					>
						{/* Back card — вторая обложка, смещена и повёрнута.
						    При hover контейнера выезжает вперёд. */}
						{audioTrackCount > 1 && (
							<span
								className="gw__audio-card gw__audio-card--back"
								style={
									gen.resultImageUrls?.[1]
										? { backgroundImage: `url(${gen.resultImageUrls[1]})` }
										: undefined
								}
								aria-hidden="true"
							>
								{!gen.resultImageUrls?.[1] && (
									<Icon id="gen-audio" size={18} />
								)}
							</span>
						)}
						{/* Front card — первая обложка, с audio для preview */}
						<span
							className="gw__audio-card gw__audio-card--front"
							style={
								gen.resultImageUrls?.[0]
									? { backgroundImage: `url(${gen.resultImageUrls[0]})` }
									: undefined
							}
						>
							{!gen.resultImageUrls?.[0] && (
								<Icon id="gen-audio" size={20} />
							)}
						</span>
						<audio
							src={gen.resultUrls?.[0] || gen.result}
							preload="metadata"
							style={{ display: 'none' }}
						/>
					</button>
				</SideTooltip>

				{/* Быстрый переход в «Мои треки» чтобы продолжить работу */}
				<SideTooltip text="Открыть в «Мои треки»">
					<button
						type="button"
						className="gw__audio-to-library"
						onClick={() => navigate('/bots/audio/library')}
						aria-label="Открыть в «Мои треки»"
					>
						<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
							<path d="M3 6 L9 6 M6 3 L9 6 L6 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
					</button>
				</SideTooltip>
			</div>
		) : isPresentation ? (
			<div className="gw__result-slot gw__result-slot--presentation">
				<PresentationResultCard
					resultUrl={gen.result}
					mode={gen.settings.presentation?.mode ?? 'edit'}
				/>
			</div>
		) : (
			<div className="gw__result-slot">
				<Tooltip {...(isVideo ? { text: t('generate.widget.openResult') } : { imagePreview: gen.result })}>
					<button
						className="gw__result-slot__inner"
						onClick={() => setResultModalOpen(true)}
						type="button"
						title={t('generate.widget.openResult')}
					>
						{isVideo ? (
							<video
								src={gen.result}
								muted
								autoPlay
								loop
								playsInline
								className="gw__result-slot__media"
							/>
						) : isVideoUrl(gen.result ?? '') ? (
							<div className="gw__result-slot__inner gw__result-slot__inner--empty">
								<Logo size={40} className="gw__result-slot__logo" />
							</div>
						) : (
							<SpoilerReveal
								key={gen.result}
								src={gen.result}
								className="gw__result-slot__media"
								alt={t('generate.widget.result')}
							/>
						)}
						<div className="gw__result-slot__hover">
							<Icon id="gen-expand" size={14} />
						</div>
					</button>
				</Tooltip>
				{/* Очистка ИМЕННО поля результата (форму не трогает): кнопка
				    сброса всего виджета неочевидна — крестик прямо на превью. */}
				<SideTooltip text={t('generate.widget.clearResult')}>
					<button
						className="gw__result-clear"
						type="button"
						aria-label={t('generate.widget.clearResult')}
						onClick={() => gen.resetResult()}
					>
						<svg width="8" height="8" viewBox="0 0 12 12" fill="none" aria-hidden="true">
							<path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
						</svg>
					</button>
				</SideTooltip>
				{!isVideo && !isAudio && (
					<SideTooltip text={t('generate.widget.createVideo')}>
						<button
							className="gw__result-create-video"
							type="button"
							aria-label={t('generate.widget.createVideo')}
							onClick={() => {
								if (!gen.result) return
								handleCreateVideo({
									id: gen.lastRequestId ?? 'result',
									previewUrl: gen.result,
									prompt: gen.prompt,
									modelId: gen.selectedModel?.id ?? '',
									modelName: gen.selectedModel?.name ?? '',
									type: 'generation',
									isVideo: false,
									createdAt: Date.now(),
								})
							}}
						>
							<CreateVideoIcon size={12} />
						</button>
					</SideTooltip>
				)}
			</div>
		)
	) : isAudio ? (
		<div className="gw__result-slot gw__result-slot--audio">
			<SideTooltip text={t('generate.audio.trackWillAppear')}>
				<div className="gw__result-slot__audio-box gw__result-slot__audio-box--empty">
					<Icon id="gen-audio" size={22} />
				</div>
			</SideTooltip>
		</div>
	) : isPresentation ? (
		<div className="gw__result-slot gw__result-slot--presentation">
			<SideTooltip text="Готовая презентация появится здесь">
				<div className="gw__result-slot__inner gw__result-slot__inner--empty gw__result-slot__inner--presentation">
					<span aria-hidden="true">📑</span>
				</div>
			</SideTooltip>
		</div>
	) : (
		<div className="gw__result-slot">
			<SideTooltip text={t('generate.widget.generationWillAppear')}>
				<div className="gw__result-slot__inner gw__result-slot__inner--empty">
					<Logo size={40} className="gw__result-slot__logo" />
				</div>
			</SideTooltip>
		</div>
	)

	const appendUploads = (
		<>
			{!isAudio && (
				<div className="gw__result-arrow" aria-hidden="true">
					→
				</div>
			)}
			{resultSlot}
		</>
	)

	// Портал в document.body: виджет фиксирован снизу (position: fixed). Если
	// оставить его в дереве страницы, любой transform/filter/will-change на
	// предке (напр. анимация входа маршрута на F5) превращает fixed в
	// «относительно предка» → виджет всплывает не к низу вьюпорта, а зависает в
	// центре/сверху. Портал делает вьюпорт гарантированным containing-block.
	return createPortal(
		<>
			<div className={`gw__backdrop${isClosing ? ' gw__backdrop--closing' : ''}`} />
			<div
				className={`gw${isClosing ? ' gw--closing' : ''}${
					isCollapsed ? ' gw--collapsed' : ''
				}${voiceFlashOn ? ' gw--voice-flash' : ''}${isToolMode ? ' gw--tool' : ''}`}
			>
				{/* Source-track pin — над виджетом, не внутри body.
				    Остаётся виден даже когда виджет свёрнут. */}
				{isAudio && <SunoSourceTrackPin />}
				<div className="gw__body">
					{/* Раньше здесь был отдельный ряд .gw__mobile-bar с
					    History-триггером на мобилке — он занимал целую строку
					    между source-pin'ом и виджетом, из-за чего пин и
					    виджет визуально отрывались друг от друга. Теперь
					    History переехал в extraActions ModelBadge (компактный
					    chip рядом с крестиком), ряд не нужен. */}

					<div className="gw__dialog">
						<div className="gw__editor-row" ref={editorRowRef}>
							<div
							ref={inputColRef}
							className="gw__input-col"
							onClick={
								isCollapsed
									? (e) => {
											// Раскрытие кликом по ВСЕЙ свёрнутой полосе (раньше —
											// только по стрелке; UI-ошибка). Живые контролы внутри
											// (идея/?/история/крестик) работают как раньше — их
											// клики не перехватываем.
											if ((e.target as HTMLElement).closest('button, a, input, [role="button"]')) return
											onExpandRequest?.()
										}
									: undefined
							}
						>
								<ModelBadge
									modelName={selectedModel!.name}
									modelIcon={selectedModel!.icon}
									leadSlot={
										/* Свёрнутый виджет с прикреплённой карточкой (Artlist:
										   «AI Headshot Generator» в слим-баре) — аватар + название.
										   Лого/цену модели в этом состоянии прячет CSS. */
										isCollapsed && attachedCard ? (
											<span className="gw__pin-chip">
												{(attachedCard.previewUrl || attachedCard.demoBeforeUrl) && (
													<img
														src={attachedCard.previewUrl || attachedCard.demoBeforeUrl}
														alt=""
														className="gw__pin-chip__avatar"
														loading="lazy"
													/>
												)}
												<span className="gw__pin-chip__title">{attachedCard.title}</span>
											</span>
										) : undefined
									}
									currentCost={costPending ? '…' : currentCost}
									isVideo={isVideo}
									requiresVideo={requiresVideo}
									requiresImage={requiresImage}
									supportsImageInput={supportsImageInput}
									isAudio={isAudio}
									isPresentation={isPresentation}
									clearDisabled={clearDisabled}
									onClear={() => {
										// Сброс = «начать заново»: прикреплённая карточка уходит
										// вместе с формой (и её атрибуция — единый канал).
										gen.resetAll()
										detachStyleCard()
									}}
									costClassName="gw__badge-cost"
									costCoinLast={true}
									compact={isToolMode}
									onHelpClick={isToolMode ? undefined : (e) => { setHelpAnchor(e?.currentTarget ?? null); setHelpOpen(true) }}
									wrapClearButton={(btn) => (
										<SideTooltip text={t('generate.widget.clear')} preferSide="left">
											{btn}
										</SideTooltip>
									)}
									extraActions={
										<>
											{/* History-chip — компактный, только на мобилке
											    (см. .gw__header-history CSS). Опенит ту же
											    mobileHistoryOpen шторку что раньше открывал
											    .gw__mobile-bar. Непрозрачный фон чтобы не
											    просвечивал контент под ним. */}
											{recent.length > 0 && (
												<SideTooltip
													text={t('generate.widget.showHistory')}
													preferSide="left"
												>
													<button
														className="gw__header-history"
														onClick={() => setMobileHistoryOpen(true)}
														type="button"
														aria-label={t('generate.history')}
													>
														<span className="gw__header-history__icon" aria-hidden="true">🕐</span>
														<span className="gw__header-history__count">{recent.length}</span>
													</button>
												</SideTooltip>
											)}
											{(onClose || onExpandRequest) && (
												<SideTooltip
													text={isCollapsed ? t('generate.widget.expandWidget') : t('generate.widget.collapseWidget')}
													preferSide="left"
												>
													<button
														className="gw__close-btn"
														onClick={
															isCollapsed ? () => onExpandRequest?.() : handleClose
														}
														type="button"
														aria-label={
															isCollapsed ? t('generate.widget.expandWidget') : t('generate.widget.collapseWidget')
														}
													>
														<svg
															width="13"
															height="13"
															viewBox="0 0 13 13"
															fill="none"
															aria-hidden="true"
														>
															<path
																d="M2 4.5L6.5 9L11 4.5"
																stroke="currentColor"
																strokeWidth="1.8"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														</svg>
													</button>
												</SideTooltip>
											)}
										</>
									}
								/>

								<div className="gw__input-col__body">
								{isToolMode ? (
									<GenerateToolMode onOpenResult={openToolResult} />
								) : (
								<ErrorBoundary>
									<GenerateInputBar
										appendUploads={appendUploads}
										onOpenResult={openToolResult}
										onIdeaWizard={
											/* Мобильная точка входа Идея-мастера — кнопка «✦T» в
											   тулбаре: пустое поле → мастер, есть текст → улучшить
											   промпт. Десктоп — пилюля по центру шапки. */
											ideaAvailable
												? (e) => { setIdeaAnchor(e.currentTarget); setIdeaOpen(true) }
												: undefined
										}
										onMotionTool={
											/* «Повторить движение» (23.10): с любой страницы виджета — в генератор, режим применит
											   ссылка ?tool=motion. Рубильник админки закрыт для зрителя, режимы карточек,
											   аудио/презентации — пилюли нет. */
											motionFits
												? () => navigate({ pathname: '/generate', search: '?tool=motion' }, { state: { motionOpen: Date.now() } })
												: undefined
										}
										topSlot={
											/* Строка-пин «Выбранный стиль». Мобила: единственное место.
											   Десктоп-картинка: скрыт (--mobileonly) — карточку там
											   показываем в ряду иконок (styleCardSlot ниже). Аудио:
											   остаётся строкой на всех ширинах. */
											attachedCard ? (
												<AttachedStyleCard
													variant="row"
													card={attachedCard}
													onDetach={detachStyleCard}
													className={`gw__style-row${isAudio ? '' : ' gw__style-row--mobileonly'}`}
													audioRefActive={isAudioReferenceActive(attachedCard, gen.selectedModel)}
												/>
											) : undefined
										}
										styleCardSlot={
											/* Десктоп-картинка: та же карточка правым блоком в ряду
											   иконок (.gm__uploads-aside, рядом с чипами). Аудио — не
											   используется (пин там остаётся topSlot-строкой). */
											attachedCard && !isAudio ? (
												<AttachedStyleCard
													variant="row"
													card={attachedCard}
													onDetach={detachStyleCard}
													className="gw__style-inrow"
													audioRefActive={isAudioReferenceActive(attachedCard, gen.selectedModel)}
												/>
											) : undefined
										}
										modeNav={
											/* Навигация по разделам — пилюля с поповером (Artlist-style)
											   в нижнем ряду бара. Reuse BottomBarPopover: портал+fixed
											   (не клипается overflow панели), outside-click, Escape.
											   Логика прежняя — handleModeSwitch(category). */
											<BottomBarPopover
												className="gw__mode-nav-pill"
												icon={
													<img
														src={currentMode.icon}
														alt=""
														width={15}
														height={15}
														className={currentMode.colored ? 'is-colored' : undefined}
													/>
												}
												label={t(currentMode.labelKey)}
												options={SECTION_MODES.map((mode) => ({
													value: mode.category,
													label: t(mode.labelKey),
													icon: (
														<img
															src={mode.icon}
															alt=""
															width={16}
															height={16}
															className={mode.colored ? 'is-colored' : undefined}
														/>
													),
												}))}
												value={currentCategory}
												onChange={handleModeSwitch}
											/>
										}
										mobileModeNav={<WidgetSectionNav />}
									/>
								</ErrorBoundary>
								)}
								</div>
							</div>

							{recent.length > 0 && (
								<div
									className={`gw__recent${historyOpen ? '' : ' gw__recent--collapsed'}`}
									ref={recentRef}
								>
									<div className="gw__recent-inner">
										<div className="gw__recent-list">
											{recent.map((item) => {
												const isAudioItem = item.kind === 'audio'
												const isPresentationItem = item.kind === 'presentation'
												return (
												<div
													key={item.id}
													className={`gw__recent-thumb${isAudioItem ? ' gw__recent-thumb--audio' : ''}${isPresentationItem ? ' gw__recent-thumb--presentation' : ''}`}
												>
													{isPresentationItem ? (
														<SideTooltip text={item.prompt || 'Презентация'} preferSide="left">
															<button
																className="gw__recent-thumb-btn gw__recent-thumb-btn--presentation"
																onClick={() => setActiveCard(item)}
																type="button"
															>
																<span className="gw__recent-pres-icon" aria-hidden="true">📑</span>
																<span className="gw__recent-pres-label">
																	{item.presentationMode === 'create' ? 'Создана' : 'Отредактирована'}
																</span>
															</button>
														</SideTooltip>
													) : isAudioItem ? (
														(() => {
															// Резолвим обложку: сохранённый previewUrl → индекс Suno.
															const coverUrl = item.previewUrl || getCover(item.audioTracks?.[0]) || ''
															const hasCover = !!coverUrl && !/\.(mp3|wav|m4a|ogg|flac)(\?|$)/i.test(coverUrl)
															return (
																<SideTooltip text={item.audioTitle || t('generate.audio.audioTrack')} preferSide="left">
																	<button
																		className={`gw__recent-thumb-btn${hasCover ? ' gw__recent-thumb-btn--with-cover' : ''}`}
																		onClick={(e) => {
																			const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
																			setModalInitialTime(audio?.currentTime ?? 0)
																			if (audio) {
																				audio.pause()
																				audio.currentTime = 0
																			}
																			setActiveCard(item)
																		}}
																		onMouseEnter={(e) => {
																			const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
																			audio?.play().catch(() => {})
																		}}
																		onMouseLeave={(e) => {
																			const audio = e.currentTarget.querySelector<HTMLAudioElement>('audio')
																			if (audio) { audio.pause(); audio.currentTime = 0 }
																		}}
																		type="button"
																	>
																		{hasCover && (
																			<img
																				src={coverUrl}
																				alt=""
																				className="gw__recent-audio-cover"
																				loading="lazy"
																				onError={(e) => {
																					e.currentTarget.style.display = 'none'
																					pruneIfMissing(item.id)
																				}}
																			/>
																		)}
																		<Icon id="gen-audio" size={14} />
																		<span className="gw__recent-audio-title">
																			{item.audioTitle || t('generate.audio.untitled')}
																		</span>
																		{item.audioTracks?.[0] && (
																			<audio
																				src={item.audioTracks[0]}
																				preload="metadata"
																				style={{ display: 'none' }}
																			/>
																		)}
																	</button>
																</SideTooltip>
															)
														})()
													) : (
														<Tooltip
															{...(item.isVideo
																? { videoPreview: item.previewUrl }
																: { imagePreview: item.previewUrl })}
														>
															<button
																className="gw__recent-thumb-btn"
																onClick={() => setActiveCard(item)}
																type="button"
															>
																{item.isVideo ? (
																	<video src={item.previewUrl} className="gw__recent-media" muted onError={() => pruneIfMissing(item.id)} />
																) : (
																	<img src={item.previewUrl} alt="" className="gw__recent-media" onError={() => pruneIfMissing(item.id)} />
																)}
															</button>
														</Tooltip>
													)}
													<SideTooltip text={t('generate.widget.reuse')}>
														<button
															className="gw__recent-reuse"
															onClick={(e) => {
																e.stopPropagation()
																handleCardAction(item)
															}}
															type="button"
															aria-label={t('generate.widget.reuse')}
														>
															<Icon id="gen-reuse" size={9} />
														</button>
													</SideTooltip>
													{!item.isVideo && !isAudioItem && !isPresentationItem && item.type === 'generation' && (
														<SideTooltip text={t('generate.widget.createVideo')}>
															<button
																className="gw__recent-create-video"
																onClick={(e) => {
																	e.stopPropagation()
																	handleCreateVideo(item)
																}}
																type="button"
																aria-label={t('generate.widget.createVideo')}
															>
																<CreateVideoIcon size={9} />
															</button>
														</SideTooltip>
													)}
													<button
														className="gw__recent-remove"
														onClick={() => removeGeneration(item.id)}
														type="button"
														aria-label={t('common.delete')}
													>
														<Icon id="gen-close" size={9} />
													</button>
												</div>
												)
											})}
										</div>
									</div>
									<button
										className="gw__history-toggle"
										onClick={() => setHistoryOpen((v) => !v)}
										type="button"
										aria-label={historyOpen ? t('generate.widget.hideHistory') : t('generate.widget.showHistory')}
									>
										<span className="gw__history-toggle__arrow">{historyOpen ? '›' : '‹'}</span>
										<span className="gw__history-toggle__label">{t('generate.history')}</span>
									</button>
								</div>
							)}
						</div>
					</div>
				</div>

				{activeCard && (
					<CardModal
						card={activeCard}
						initialTime={modalInitialTime}
						onClose={() => setActiveCard(null)}
						onPrimaryAction={handleCardAction}
						onCreateVideo={handleCreateVideo}
					/>
				)}

				<Sheet
					variant="bottom"
					showHandle
					open={mobileHistoryOpen}
					onClose={() => setMobileHistoryOpen(false)}
					title={t('generate.recentGenerations')}
				>
					<CardGallery
						items={recent}
						onPrimaryAction={(card) => {
							handleCardAction(card)
							setMobileHistoryOpen(false)
						}}
						onQuickAction={(card) => {
							handleCardAction(card)
							setMobileHistoryOpen(false)
						}}
						onCreateVideo={(card) => {
							handleCreateVideo(card)
							setMobileHistoryOpen(false)
						}}
						onRemove={removeGeneration}
						onMediaError={pruneIfMissing}
					/>
				</Sheet>


				{resultModalOpen && gen.result && gen.selectedModel && (
					<CardModal
						card={{
							id: gen.lastRequestId ?? 'current',
							type: 'generation',
							// Для аудио берём обложку из resultImageUrls (пришла из polling
							// через TaskStatusResponse.image_urls → suno_tracks.image_url).
							// Для presentation — пусто (PPTX не превьюшится в браузере,
							// CardModal рендерит иконку через ветку по kind).
							previewUrl: isAudio
								? (gen.resultImageUrls?.[0] ?? '')
								: isPresentation
									? ''
									: (openedResult ?? gen.result),
							sourceImageUrl: gen.uploadedImage?.preview ?? undefined,
							// GTM (tool-режим карточки): промпт системный — в модал не
							// передаём вовсе, promptLocked прячет там промпт и модель.
							prompt: isToolMode ? '' : gen.prompt,
							promptLocked: isToolMode || undefined,
							modelId: gen.selectedModel.id,
							modelName: gen.selectedModel.name,
							isVideo,
							kind: isPresentation
								? 'presentation'
								: isAudio
									? 'audio'
									: isVideo
										? 'video'
										: 'image',
							createdAt: Date.now(),
							...(isAudio && {
								audioTracks: gen.resultUrls || [gen.result],
								audioTitle: gen.settings.audioTitle,
								audioStyle: gen.settings.audioStyle,
								audioInstrumental: gen.settings.audioInstrumental,
								audioVocalGender: gen.settings.audioVocalGender,
								audioCustomMode: gen.settings.audioCustomMode,
							}),
							...(isPresentation && {
								pptxUrl: gen.result,
								presentationMode: gen.settings.presentation?.mode ?? 'edit',
							}),
							settings: {
								aspectRatio: gen.settings.aspectRatio,
								resolution: gen.settings.resolution,
								duration: gen.settings.duration,
								outputFormat: gen.settings.outputFormat,
							},
						}}
						initialTime={modalInitialTime}
						onClose={closeResultModal}
						onPrimaryAction={() => {
							closeResultModal()
							gen.handleGenerate()
						}}
						onCreateVideo={(card) => {
							closeResultModal()
							handleCreateVideo(card)
						}}
					/>
				)}
			</div>

			{selectedModel && (
				<ModelHelpSheet
					open={helpOpen}
					onClose={() => setHelpOpen(false)}
					anchorEl={helpAnchor}
					adapter={selectedModel.adapter || selectedModel.provider}
					modelId={selectedModel.id}
					modelName={selectedModel.name}
					modelIcon={selectedModel.icon}
					category={selectedModel.category}
					modeLabel={modelModeLabel({ modelName: selectedModel.name, isVideo, requiresVideo, requiresImage, isAudio, isPresentation })}
					onStartTour={() => { setHelpOpen(false); openTour() }}
					scenarios={selectedModel.scenarios}
					characterFit={selectedModel.characterFit}
					promptRefs={promptRefsHelpFor(selectedModel)}
				/>
			)}

			{selectedModel && (
				<IdeaWizardSheet
					open={ideaOpen}
					onClose={() => setIdeaOpen(false)}
					anchorEl={ideaAnchor}
					model={selectedModel}
					onInsert={(prompt, s) => {
						// Рекомендованные настройки мастера → виджет (1:1 c /generate).
						if (s.aspect_ratio) gen.userUpdateSetting('aspectRatio', s.aspect_ratio)
						if (s.duration) gen.userUpdateSetting('duration', s.duration)
						if (s.sound !== null && s.sound !== undefined) gen.userUpdateSetting('sound', s.sound)
						gen.setPrompt(prompt)
						// Вставили идею в свёрнутый виджет → раскрываем: юзер сразу
						// видит, что произошло (тот же приём, что у обучалки выше).
						if (isCollapsed) onExpandRequest?.()
					}}
				/>
			)}

			{tourVisible && (
				<SpotlightTour
					steps={buildWidgetTourSteps({
						prompt: gen.prompt,
						uploadedImage: gen.uploadedImage,
						referenceImages: gen.referenceImages,
						uploadedVideo: gen.uploadedVideo,
						isLoading: gen.isLoading,
						result: gen.result,
						requiresImage: gen.requiresImage,
						requiresTwoImages: gen.requiresTwoImages,
						requiresVideo: gen.requiresVideo,
						supportsMultipleImages: gen.supportsMultipleImages,
						isAudio: gen.isAudio,
						isPresentation: gen.isPresentation,
						noPrompt: gen.selectedModel?.noPrompt,
					})}
					storageKey={BOTS_WIDGET_TOUR_KEY}
					onFinish={() => setTourVisible(false)}
				/>
			)}
		</>,
		document.body,
	)
})
